import { Observable, of, from as fromPromise, isObservable } from 'rxjs';
import { from } from 'rxjs';
import { first, mergeAll, map, every } from 'rxjs/operators';

import { Injectable, InjectionToken, Injector, OnDestroy, Type } from '@angular/core';

import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot } from '@angular/router';

// Helper function to check if a value is a Promise
function isPromise(value: any): value is Promise<any> {
  return !!value && typeof value.then === 'function';
}

/**
 * ANDs Observables by merging all input observables, reducing to an Observable verifying all
 * input Observables return `true`.
 */
function andObservables(observables: Observable<Observable<any>>): Observable<boolean> {
  return observables.pipe(
    mergeAll(),
    every((result: any) => result === true)
  );
}
function wrapIntoObservable<T>(value: T | Promise<T> | Observable<T>): Observable<T> {
  if (isObservable(value)) {
    return value;
  }

  if (isPromise(value)) {
    // Use `Promise.resolve()` to wrap promise-like instances.
    // Required ie when a Resolver returns a AngularJS `$q` promise to correctly trigger the
    // change detection.
    return fromPromise(Promise.resolve(value));
  }

  return of(value as T);
}

let blockId = 0;

/**
 * A global routing guard that act as a route guard host.
 * The global routing guard doesn't have blocking logic, instead it allow other guards to register as
 * child blockers or register ad-hoc blockers.
 *
 */
@Injectable()
export class GlobalRouteGuard implements CanActivateChild, OnDestroy {
  private children = new Set<CanActivate>();
  private blockers = new Set<number>();

  constructor(private router: Router, private injector: Injector) {}

  /**
   * Use an [[Injector}} to get an instance of the given `token` and call `addChildGuard` with it.
   * @param token
   * @return {boolean}
   */
  provideChildGuard<T extends CanActivate>(token: Type<T> | InjectionToken<T>): boolean {
    const instance = this.injector.get(token, <any>false);
    if (!instance) {
      return false;
    } else {
      this.addChildGuard(instance);
      return true;
    }
  }

  /**
   * Add a child guard.
   * On an activation event all guards are called in the order they we're added, if one fails the
   * whole activation fails.
   * @param instance
   */
  addChildGuard(instance: any): void {
    this.children.add(instance);
  }

  /**
   * Create a blocker based on a stream of boolean values which set's the block state.
   * @param value
   * @param initialState The initial block state. If you are using an observable hooked to a simple
   * [[Subject]] then calling `block` will do nothing but bind the streams, this allows setting the
   * block state before getting a single item from the stream.
   * > If the stream source is [[BehaviouralSubject]] or any other type that emits on subscription
   * setting this value is not required.
   */
  block(value: Observable<boolean>, initialState?: boolean): void;
  /**
   * Create a transient blocker, based on a promise.
   * When the promise resolves the blocking is removed.
   * @param value
   */
  block(value: Promise<any | void>): void;
  /**
   * Create a transient blocker that requires manual operation to unblock.
   * A function is returned, invoke it to remove the blocking.
   *
   * > Loosing the reference to the function will create a stale blocker that can not be removed.
   */
  block(): Function;
  block(value?: Observable<boolean> | Promise<any | void>, initialState?: boolean): void | Function {
    if (!value) {
      return this.blockReleaseFactory();
    }
    if (isPromise(value)) {
      const unblocker: any = this.blockReleaseFactory();
      value.then(unblocker).catch(unblocker);
    }
    if (isObservable(value)) {
      const unblocker = this.blockReleaseFactory(true, initialState);
      value.subscribe(
        (isBlocking) => (isBlocking ? unblocker.block() : unblocker()),
        () => unblocker(),
        () => unblocker()
      );
    }
  }

  /**
   * Catch-all method to remove all blockers
   */
  unblockAll(): void {
    this.blockers.clear();
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean> {
    if (this.blockers.size > 0) {
      return false;
    }

    if (this.children.size > 0) {
      return andObservables(
        from(this.children).pipe(
          map((guard) => {
            return wrapIntoObservable(guard.canActivate(route, state)).pipe(first());
          })
        )
      );
    }

    return true;
  }

  ngOnDestroy(): void {
    // TODO: get a ref to all subscriptions of Observable blockers and unsubscribe
  }

  private releaseBlockId(id: number): void {
    this.blockers.delete(id);
  }

  private blockReleaseFactory(dynamic: true, initialState: boolean): (() => {}) & { block: Function };
  private blockReleaseFactory(): Function;
  private blockReleaseFactory(dynamic?: true, initialState?: boolean): Function | ((() => {}) & { block: Function }) {
    const id = blockId++;

    const unblocker = () => this.releaseBlockId(id);

    if (!dynamic || initialState === true) {
      this.blockers.add(id);
    }

    if (dynamic === true) {
      (<{ block: Function }>(<any>unblocker)).block = () => this.blockers.add(id);
    }

    return unblocker;
  }
}
